// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

interface CopilotMessage {
    role: string;
    content: string;
}

interface CopilotChunk {
    model: string;
    created_at: string;
    message: CopilotMessage;
    done: boolean;
    able_to_answer: boolean;
    chat_id: string;
    message_id: number;
}

type ParseResult = {
    objects: CopilotChunk[];
    buffer: string;
};

const isCopilotChunk = (obj: unknown): obj is CopilotChunk => typeof obj === 'object' &&
    obj !== null &&
    typeof (obj as Partial<CopilotChunk>).model === 'string' &&
    typeof (obj as Partial<CopilotChunk>).created_at === 'string' &&
    typeof (obj as Partial<CopilotChunk>).message === 'object' &&
    typeof (obj as Partial<CopilotChunk>).message?.role === 'string' &&
    typeof (obj as Partial<CopilotChunk>).message?.content === 'string' &&
    typeof (obj as Partial<CopilotChunk>).able_to_answer === 'boolean' &&
    typeof (obj as Partial<CopilotChunk>).done === 'boolean' &&
    typeof (obj as Partial<CopilotChunk>).chat_id === 'string' &&
    typeof (obj as Partial<CopilotChunk>).message_id === 'number';

const parseJsonChunks = (chunk: string, buffer: string = ''): ParseResult => {
    const result: CopilotChunk[] = [];
    let start = -1;
    let braceCount = 0;
    let inString = false;
    let escape = false;
    const data = buffer + chunk;

    for (let i = 0; i < data.length; i++) {
        const char = data[i];
        if (inString) {
            if (escape) {
                escape = false;
            } else if (char === '\\') {
                escape = true;
            } else if (char === '"') {
                inString = false;
            }
        } else if (char === '"') {
            inString = true;
        } else if (char === '{') {
            if (braceCount === 0) {
                start = i;
            }
            braceCount++;
        } else if (char === '}') {
            braceCount--;
            if (braceCount === 0 && start !== -1) {
                const objStr = data.slice(start, i + 1);
                try {
                    const obj = JSON.parse(objStr);
                    if (isCopilotChunk(obj)) {
                        result.push(obj);
                    }
                } catch {
                    // ignore parse errors
                }
                start = -1;
            }
        }
    }

    const newBuffer = (braceCount > 0 && start !== -1) ? data.slice(start) : '';
    return { objects: result, buffer: newBuffer };
};

export default parseJsonChunks;
